/**
 * Advanced-filter clauses for the customer list's State filter — METALARK-1015
 *
 * The list used to bucket its status pills with a bare
 * `subscriptions.$any.status` match. That disagrees with the State column: an
 * `active` row whose billing period has lapsed is Past Due, so "Active" listed
 * past-due customers — the ticket's reported bug.
 *
 * The fix filters on `Subscription.state`, the canonical lifecycle column added
 * by METALARK-789. Every value the State column renders is a value that column
 * stores, so filter and badge agree by construction rather than by keeping a
 * SQL mirror of the derivation in sync with the TypeScript one.
 *
 * Two properties of that column make this work:
 *
 *  - It is a plain String, so a state filter compiles to `"state" IN ($1, ...)`
 *    with no timestamp comparison and therefore no change to the shared
 *    FilterQueryCompiler (used by 74 generated services).
 *  - SubscriptionMaintenanceService advances it daily for every time-based
 *    transition — trial expiry, payment failure, grace expiry, deletion
 *    marking — explicitly as a webhook fallback, and backfills NULL rows.
 *
 * The State column is rendered from the same column via displayedFromState()
 * (see customer-table.tag). Keep those two in step: filtering on one source
 * while labelling from another is what produced the original bug.
 *
 * Two constraints of FilterQueryCompiler still shape the output:
 *
 *  - `$any` compiles to an INNER JOIN, not EXISTS. The state match and the
 *    churn window must live inside ONE `$any`; separate top-level `$any`
 *    clauses would each add their own JOIN and cross-multiply rows.
 *  - That same INNER JOIN drops customers with no subscription rows at all, so
 *    NO_SUBSCRIPTION can only be expressed via `$none` on its own. It cannot be
 *    OR'd with the others in a single query — callers must treat it as
 *    mutually exclusive (see stateFilterFor()).
 *
 * Known limitation: a subscription row whose `state` is still NULL (never
 * touched by a transition and not yet reached by the backfill sweep) matches no
 * state filter, so it appears only under "All". The sweep closes this window.
 */

import { DisplayedSubscriptionState } from './displayedSubscriptionState.js';

const iso = (ms) => new Date(ms).toISOString();

/** Conjunction that survives being nested inside `$or` — the compiler flattens bare objects. */
const all = (...clauses) => ({ $and: clauses.filter(Boolean) });

/**
 * Displayed state -> the `Subscription.state` value(s) that produce it.
 *
 * All but one are the same string on both sides. TRIAL_ENDING_SOON is the
 * exception: it is not stored, it is a display-only refinement of TRIAL_ACTIVE
 * applied when the trial ends within the configured window. It is therefore not
 * independently filterable and is absent from SELECTABLE_STATES; selecting
 * "Trial Active" returns those rows too, and they keep their own badge.
 */
const STORED_STATE_FOR = {
    [DisplayedSubscriptionState.ACTIVE]: 'ACTIVE',
    [DisplayedSubscriptionState.ACTIVE_SCHEDULED_CANCEL]: 'ACTIVE_SCHEDULED_CANCEL',
    [DisplayedSubscriptionState.TRIAL_ACTIVE]: 'TRIAL_ACTIVE',
    [DisplayedSubscriptionState.TRIAL_ENDING_SOON]: 'TRIAL_ACTIVE',
    [DisplayedSubscriptionState.TRIAL_EXPIRED]: 'TRIAL_EXPIRED',
    [DisplayedSubscriptionState.PAST_DUE]: 'PAST_DUE',
    [DisplayedSubscriptionState.PAYMENT_PENDING]: 'PAYMENT_PENDING',
    [DisplayedSubscriptionState.INCOMPLETE]: 'INCOMPLETE',
    [DisplayedSubscriptionState.CANCELED_IN_GRACE]: 'CANCELED_IN_GRACE',
    [DisplayedSubscriptionState.EXPIRED]: 'EXPIRED',
    [DisplayedSubscriptionState.PENDING_DELETION]: 'PENDING_DELETION',
};

/**
 * The stored `state` value(s) a displayed state selects, deduplicated.
 * Returns [] for NO_SUBSCRIPTION, which is not a row-level predicate.
 */
export function storedStatesFor(states) {
    const mapped = (states || [])
        .map(s => STORED_STATE_FOR[s])
        .filter(Boolean);
    return [...new Set(mapped)];
}

/** Trailing window the "Churned" toggle uses, in days. */
export const CHURN_WINDOW_DAYS = 30;

/**
 * Matches no customer. Used for filter combinations that are contradictory by
 * definition — customerId is the primary key, so it is never null.
 */
const NEVER_MATCHES = { customerId: { $isNull: true } };

/**
 * Compose the customer-level filter for a set of displayed states, optionally
 * narrowed to customers who churned recently.
 *
 * `options.churnedWithinDays` adds a canceledAt window. It is AND'ed INTO the
 * same `$any` as the state match rather than added as a second relation clause —
 * two `$any` clauses would each contribute their own JOIN and multiply the
 * result rows. Sharing one `$any` also gives the stricter, more useful reading:
 * the subscription that matches the selected state is the same one that was
 * canceled recently.
 *
 * NO_SUBSCRIPTION wins outright when present: `$none` and `$any` cannot be OR'd
 * because the `$any` JOIN would exclude the very rows `$none` is looking for.
 * The UI enforces this by clearing the other states when it is picked. Combined
 * with a churn window it is contradictory — a customer with no subscription
 * rows cannot have one canceled recently — so that pairing matches nothing.
 */
export function stateFilterFor(states, now = new Date(), options = {}) {
    const selected = (states || []).filter(Boolean);
    const churnDays = options.churnedWithinDays;
    const churned = typeof churnDays === 'number' && churnDays > 0;

    if (selected.length === 0 && !churned) return null;

    if (selected.includes(DisplayedSubscriptionState.NO_SUBSCRIPTION)) {
        return churned ? NEVER_MATCHES : { subscriptions: { $none: { id: { $isNull: false } } } };
    }

    const stored = storedStatesFor(selected);
    const stateInner = stored.length === 0 ? null : { state: { $in: stored } };

    // canceledAt ends in `At`, which the compiler already casts to timestamp.
    const churnInner = churned
        ? { canceledAt: { $gte: iso(now.getTime() - churnDays * 86400000) } }
        : null;

    const inner = (stateInner && churnInner)
        ? all(stateInner, churnInner)
        : (stateInner || churnInner);

    if (!inner) return null;

    return { subscriptions: { $any: inner } };
}

/**
 * Dropdown order — Active first, then trials, then the failure states roughly
 * by urgency. NO_SUBSCRIPTION sits last because picking it clears the rest.
 *
 * Absent on purpose: TRIAL_ENDING_SOON (a display-only refinement of
 * TRIAL_ACTIVE — see STORED_STATE_FOR), and UNPAID / PAUSED / SOFT_DELETED,
 * which the lifecycle stores as PAST_DUE / CANCELED_IN_GRACE / a deleted row
 * respectively and the State column therefore never shows.
 */
export const SELECTABLE_STATES = [
    DisplayedSubscriptionState.ACTIVE,
    DisplayedSubscriptionState.ACTIVE_SCHEDULED_CANCEL,
    DisplayedSubscriptionState.TRIAL_ACTIVE,
    DisplayedSubscriptionState.TRIAL_EXPIRED,
    DisplayedSubscriptionState.PAST_DUE,
    DisplayedSubscriptionState.PAYMENT_PENDING,
    DisplayedSubscriptionState.INCOMPLETE,
    DisplayedSubscriptionState.CANCELED_IN_GRACE,
    DisplayedSubscriptionState.EXPIRED,
    DisplayedSubscriptionState.PENDING_DELETION,
    DisplayedSubscriptionState.NO_SUBSCRIPTION,
];

/**
 * The quick-filter pills, expressed as the state sets they select. 'churned' is
 * absent on purpose: it is a canceledAt window, not a state set, and is applied
 * via the churnedWithinDays option instead.
 *
 * 'attention' is every STORED state urgencyForState() scores above zero. It
 * cannot be exactly the urgency ordering: TRIAL_ENDING_SOON scores 80 and sorts
 * near the top, but it is a display label rather than a stored state (see
 * STORED_STATE_FOR), so the pill cannot ask the database for it. A trial ending
 * within the window therefore sorts high without being caught by the pill.
 * Adding TRIAL_ACTIVE here instead would sweep in every live trial, which is a
 * worse answer than missing the few that are close to ending.
 */
export const STATE_PRESETS = {
    active: [
        DisplayedSubscriptionState.ACTIVE,
        DisplayedSubscriptionState.ACTIVE_SCHEDULED_CANCEL,
    ],
    trial: [
        DisplayedSubscriptionState.TRIAL_ACTIVE,
    ],
    attention: [
        DisplayedSubscriptionState.PAST_DUE,
        DisplayedSubscriptionState.CANCELED_IN_GRACE,
        DisplayedSubscriptionState.PENDING_DELETION,
        DisplayedSubscriptionState.EXPIRED,
        DisplayedSubscriptionState.PAYMENT_PENDING,
        DisplayedSubscriptionState.INCOMPLETE,
        DisplayedSubscriptionState.ACTIVE_SCHEDULED_CANCEL,
        DisplayedSubscriptionState.TRIAL_EXPIRED,
    ],
};

const sameSet = (a, b) =>
    a.length === b.length && [...a].sort().join('|') === [...b].sort().join('|');

/**
 * Reverse of STATE_PRESETS: which pill, if any, does this selection represent?
 * Returns 'all' for an empty selection, or null when the selection is a set no
 * pill covers (the pill row then shows nothing highlighted).
 */
export function presetForStates(states) {
    const selected = (states || []).filter(Boolean);
    if (selected.length === 0) return 'all';
    for (const [key, preset] of Object.entries(STATE_PRESETS)) {
        if (sameSet(selected, preset)) return key;
    }
    return null;
}
